'use client';

import { PlusIcon } from '@react/chakra/core';
import { useSuspenseInfiniteQuery } from '@react/utils/queryHooks';
import {
  Box,
  Show,
  Flex,
  Text,
  Grid,
  Stack,
  Button,
  HStack,
  type GridProps,
} from '@react/chakra';

import type { JSX } from 'react';

import { useTopicQuery } from '~/features/topics/hooks';

import { postsOptions } from '../queries';
import { PostListSkeleton } from './PostListSkeleton';
import { Empty, Post, PostSkeleton } from './components';

export function PostList(props: GridProps): JSX.Element {
  const [topicQuery] = useTopicQuery();

  const { data, fetchNextPage, isRefetching, isFetchingNextPage } =
    useSuspenseInfiniteQuery(
      postsOptions({
        topics: topicQuery || [],
      }),
    );

  const lastData = data.pages[data.pages.length - 1];
  const { data: currentData = [], metadata } = lastData || {};
  const { total = 0, currentTotal = 0, perPage = 0 } = metadata || {};

  if (isRefetching) {
    return <PostListSkeleton />;
  }

  if (!currentData.length) {
    return <Empty />;
  }

  return (
    <Stack gap={28}>
      <Grid
        gap={6}
        autoRows='1fr'
        templateColumns={{
          md: 'repeat(3, 1fr)',
          sm: 'repeat(2, 1fr)',
          base: 'repeat(1, 1fr)',
        }}
        {...props}
      >
        {data.pages.map((page) => {
          return page.data.map((post) => {
            return <Post key={post.slug} data={post} />;
          });
        })}
        <Show when={isFetchingNextPage}>
          <PostSkeleton />
          <PostSkeleton />
          <PostSkeleton />
        </Show>
      </Grid>
      <Flex alignItems='center' justifyContent='center'>
        <Show when={(data.pages.length - 1) * perPage + currentTotal < total}>
          <Button
            h='unset'
            color='white'
            pos='relative'
            variant='plain'
            display='inline-flex'
            transitionDuration='.15s'
            onClick={() => fetchNextPage()}
            transitionTimingFunction='cubic-bezier(.4, 0, .2,1)'
            transitionProperty='color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter'
            _hover={{
              '& > .ring': {
                boxShadow: 'eclipse',
                borderColor: 'transparent',
              },
            }}
          >
            <Box
              top={0}
              left={0}
              right={0}
              bottom={0}
              pos='absolute'
              className='ring'
              border='2px solid'
              borderRadius='9999px'
              borderColor='gray.600'
              transitionDuration='.3s'
              transitionTimingFunction='cubic-bezier(.4, 0, .2,1)'
              transitionProperty='color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter'
            />
            <HStack gap={4} py={5} px={10} pos='relative'>
              <Text fontSize='1.2rem'>Load more</Text>
              <PlusIcon h='16px' w='16px' fill='white' />
            </HStack>
          </Button>
        </Show>
      </Flex>
    </Stack>
  );
}
